'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import I18n from 'I18n';
import PortalIdParser from 'PortalIdParser';
import { SLINKY } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIStatusTag from 'UIComponents/tag/UIStatusTag';
import getTrackingStatus, { TRACKING_STATUS } from 'AdsUI/lib/trackingStatus';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { convertTimeRangeToMessage } from 'AdsUI/lib/Utils';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import { EntityTypes } from 'AdsUI/lib/Constants';
import CampaignTrackingErrorModal from 'AdsUI/components/tracking-error-modal/CampaignTrackingErrorModal';
import AdGroupTrackingErrorModal from 'AdsUI/components/tracking-error-modal/AdGroupTrackingErrorModal';
import AdTrackingErrorModal from 'AdsUI/components/tracking-error-modal/AdTrackingErrorModal';
import UITableHoverContent from 'UIComponents/table/UITableHoverContent';
import KnowledgeBaseLink from 'AdsUI/components/KnowledgeBaseLink';
import { KBArticles } from 'AdsUI/lib/Constants';
const LEAD_SYNC_SETTINGS_URL = `/settings/${PortalIdParser.get()}/marketing/ads/leadSyncing`;
const AD_SETTINGS_URL = `/settings/${PortalIdParser.get()}/marketing/ads/adAccounts`;

const TrackingFooter = ({
  children
}) => /*#__PURE__*/_jsxs("div", {
  className: "m-top-3 justify-between align-center",
  children: [children, /*#__PURE__*/_jsx(KnowledgeBaseLink, {
    kbArticle: KBArticles.AD_TRACKING,
    context: "tracking-status-tag-footer",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "campaigns.trackingStatus.footer.learnMore"
    })
  })]
});

const TrackingErrorSummary = ({
  entityType,
  trackingErrors,
  onViewErrors
}) => {
  const activeTrackingErrors = trackingErrors.filter(trackingError => !trackingError.dismissedAt);

  if (!activeTrackingErrors.size) {
    return null;
  }

  return /*#__PURE__*/_jsxs("div", {
    className: "m-y-2",
    children: [entityType !== EntityTypes.AD && entityType !== EntityTypes.KEYWORD && /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaigns.trackingStatus.summary",
        options: {
          count: activeTrackingErrors.size
        }
      })
    }), /*#__PURE__*/_jsx(TrackingFooter, {
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "tertiary",
        size: "xs",
        onClick: onViewErrors,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaigns.trackingStatus.viewErrors"
        })
      })
    })]
  });
};

const TrackingDateInfo = ({
  adAccount,
  timeRange
}) => {
  const trackingEnabledMoment = I18n.moment(adAccount.trackingSince);
  const daysTracked = I18n.moment().diff(trackingEnabledMoment, 'days');
  return /*#__PURE__*/_jsxs(UIList, {
    styled: true,
    children: [daysTracked ? /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaigns.trackingStatus.trackingEnabled.since",
        options: {
          trackingDate: trackingEnabledMoment.format('l')
        }
      }), /*#__PURE__*/_jsxs("span", {
        style: {
          color: SLINKY
        },
        children: [' - ', /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaigns.trackingStatus.trackingEnabled.daysAgo",
          options: {
            count: daysTracked
          }
        })]
      })]
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "campaigns.trackingStatus.trackingEnabled.zeroDaysAgo"
    }), /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaigns.trackingStatus.filteredDateRange"
      }), convertTimeRangeToMessage(timeRange)]
    })]
  });
};

const TrackingDisabled = () => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx("p", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "campaigns.trackingStatus.trackingDisabled"
    })
  }), /*#__PURE__*/_jsx(TrackingFooter, {
    children: /*#__PURE__*/_jsx(UILink, {
      href: AD_SETTINGS_URL,
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaigns.trackingStatus.footer.turnTrackingOn"
      })
    })
  })]
});

const TrackingPending = ({
  adAccount: {
    network
  },
  entityType
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(FormattedMessage, {
    message: `campaigns.trackingStatus.trackingPending.${network}.${entityType}`
  }), /*#__PURE__*/_jsx(TrackingFooter, {})]
});

const TrackingLimited = ({
  adAccount,
  timeRange
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(FormattedMessage, {
    message: "campaigns.trackingStatus.trackingLimited"
  }), /*#__PURE__*/_jsx(TrackingDateInfo, {
    adAccount: adAccount,
    timeRange: timeRange
  }), /*#__PURE__*/_jsx(TrackingFooter, {})]
});

const TrackingLeadGen = ({
  adAccount: {
    network
  }
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `campaigns.trackingStatus.leadGenTracking.${network}_jsx`,
    elements: {
      Link: UILink
    },
    options: {
      external: true,
      href: LEAD_SYNC_SETTINGS_URL
    }
  }), /*#__PURE__*/_jsx(TrackingFooter, {})]
});

const TrackingError = ({
  entityType,
  trackingErrors,
  onViewErrors
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(FormattedMessage, {
    message: "campaigns.trackingStatus.cannotTrack"
  }), /*#__PURE__*/_jsx(TrackingErrorSummary, {
    entityType: entityType,
    trackingErrors: trackingErrors,
    onViewErrors: onViewErrors
  })]
});

const TrackingDismissed = () => null;

const TrackingActive = ({
  adAccount,
  timeRange
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx(TrackingDateInfo, {
    adAccount: adAccount,
    timeRange: timeRange
  }), /*#__PURE__*/_jsx(TrackingFooter, {})]
});

const MAP_TRACKING_STATUS_TO_TAG = {
  [TRACKING_STATUS.DISABLED]: {
    use: 'disabled',
    TooltipMessage: TrackingDisabled
  },
  [TRACKING_STATUS.PENDING]: {
    use: 'active',
    TooltipMessage: TrackingPending
  },
  [TRACKING_STATUS.LIMITED]: {
    use: 'active',
    TooltipMessage: TrackingLimited
  },
  [TRACKING_STATUS.LEAD_GEN]: {
    use: 'active',
    TooltipMessage: TrackingLeadGen
  },
  [TRACKING_STATUS.ERROR]: {
    use: 'danger',
    TooltipMessage: TrackingError
  },
  [TRACKING_STATUS.DISMISSED]: {
    use: 'warning',
    TooltipMessage: TrackingDismissed
  },
  [TRACKING_STATUS.ACTIVE]: {
    use: 'active',
    TooltipMessage: TrackingActive
  }
};
const EntityTypeTrackingModalMap = {
  [EntityTypes.CAMPAIGN]: CampaignTrackingErrorModal,
  [EntityTypes.ADGROUP]: AdGroupTrackingErrorModal,
  [EntityTypes.AD]: AdTrackingErrorModal,
  [EntityTypes.KEYWORD]: AdTrackingErrorModal
};
const SHOW_ON_HOVER = [TRACKING_STATUS.ACTIVE, TRACKING_STATUS.PENDING, TRACKING_STATUS.LIMITED, TRACKING_STATUS.LEAD_GEN, TRACKING_STATUS.DISMISSED];

const TrackingStatusTag = ({
  adAccount,
  campaignEntity,
  adGroupEntity,
  adEntity,
  trackingStatus,
  timeRange,
  entityType
}) => {
  const status = getTrackingStatus(adAccount, campaignEntity, adGroupEntity, adEntity, trackingStatus, timeRange);
  const handleTooltipOpen = useCallback(({
    target: {
      value
    }
  }) => {
    if (value) {
      usageTracker.track('trackingStatusSurveyAction', {
        action: 'Displayed tracking status (on hover)',
        status,
        source: 'Name column',
        isTrackingErrorsModal: true
      });
    }
  }, [status]);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    if (status === TRACKING_STATUS.NOT_APPLICABLE) {
      return;
    }

    usageTracker.track('trackingStatusSurveyAction', {
      action: 'Rendered tracking status cell on screen',
      status,
      isTrackingErrorsModal: true
    });
  }, [status]);

  if (status === TRACKING_STATUS.NOT_APPLICABLE) {
    return null;
  }

  const {
    TooltipMessage,
    use
  } = MAP_TRACKING_STATUS_TO_TAG[status];
  const EntityTrackingErrorModal = EntityTypeTrackingModalMap[entityType];
  const Wrapper = SHOW_ON_HOVER.indexOf(status) > -1 ? UITableHoverContent : Fragment;
  return /*#__PURE__*/_jsxs(Wrapper, {
    children: [/*#__PURE__*/_jsx(UITooltip, {
      use: "longform",
      title: /*#__PURE__*/_jsx(TooltipMessage, {
        adAccount: adAccount,
        entityType: entityType,
        timeRange: timeRange,
        trackingErrors: trackingStatus.statuses,
        onViewErrors: () => setModalOpen(true)
      }),
      onOpenChange: handleTooltipOpen,
      disabled: status === TRACKING_STATUS.DISMISSED,
      children: /*#__PURE__*/_jsxs(UIStatusTag, {
        use: use,
        truncateStringProps: {
          useFlex: true,
          tooltip: false
        },
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: `campaigns.trackingStatus.status.${status}`
        }), status !== TRACKING_STATUS.DISMISSED && /*#__PURE__*/_jsx(UIIcon, {
          name: "info",
          className: "m-left-1"
        })]
      })
    }), modalOpen && /*#__PURE__*/_jsx(EntityTrackingErrorModal, {
      accountId: adAccount.accountId,
      adGroupId: adGroupEntity && adGroupEntity.id,
      adId: adEntity && adEntity.id,
      campaignId: campaignEntity.id,
      name: adEntity ? adEntity.name : campaignEntity.name,
      network: adAccount.network,
      onClose: () => setModalOpen(false)
    })]
  });
};

export default TrackingStatusTag;